// apps/web/src/pages/maintenance/ChecklistDetailPage.tsx
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Pencil, Trash2, UserPlus, Plus, X, ToggleLeft, ToggleRight, Check,
  Users, Clock,
} from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { formatDate } from '../../lib/utils'
import api from '../../lib/api'

// ---- Types ----
interface Asset { id: string; name: string; assetType: string | null }
interface Operation {
  id: string; stepOrder: number; title: string; description: string | null
  requiresPhoto: boolean; expectedOutcome: string | null
  asset: Asset | null
}
interface Template {
  id: string; name: string; description: string | null; frequency: string
  customerId: string | null; siteId: string | null; isActive: boolean
  operations: Operation[]
  createdAt: string
}
interface Technician { id: string; firstName: string; lastName: string; role: string }
interface Execution {
  id: string
  templateId: string
  technicianId: string | null
  supportTechnicianIds: string[]
  status: string
  createdAt: string
  technician?: Technician
}

const FREQ_LABELS: Record<string, string> = {
  once: 'Una tantum', daily: 'Giornaliera', weekly: 'Settimanale',
  monthly: 'Mensile', quarterly: 'Trimestrale', semestrale: 'Semestrale', yearly: 'Annuale',
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending:     { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800' },
  in_progress: { label: 'In corso',  color: 'bg-blue-100 text-blue-800' },
  completed:   { label: 'Completata', color: 'bg-green-100 text-green-800' },
  signed:      { label: 'Firmata',   color: 'bg-purple-100 text-purple-800' },
}

// ---- Assign modal with support technicians ----
function AssignModal({ templateId, onClose, allTechnicians }: {
  templateId: string
  onClose: () => void
  allTechnicians: Technician[]
}) {
  const qc = useQueryClient()
  const [primaryId, setPrimaryId] = useState('')
  const [supportIds, setSupportIds] = useState<string[]>([])

  const assignMutation = useMutation({
    mutationFn: () => api.post('/maintenance/executions', {
      templateId,
      technicianId: primaryId || undefined,
      supportTechnicianIds: supportIds.length > 0 ? supportIds : undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-executions', templateId] })
      onClose()
    },
  })

  function toggleSupport(id: string) {
    setSupportIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const available = allTechnicians.filter(t => t.id !== primaryId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-2xl bg-[var(--fc-surface)] shadow-2xl mx-4">
        <div className="flex items-center justify-between border-b border-[var(--fc-border)] px-5 py-4">
          <h3 className="text-base font-semibold text-[var(--fc-foreground)]">Assegna a tecnico</h3>
          <button onClick={onClose} className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Tecnico principale */}
          <div>
            <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">
              Tecnico responsabile *
            </label>
            <select
              value={primaryId}
              onChange={e => { setPrimaryId(e.target.value); setSupportIds(prev => prev.filter(x => x !== e.target.value)) }}
              className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
            >
              <option value="">— seleziona tecnico —</option>
              {allTechnicians.map(t => (
                <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>
              ))}
            </select>
          </div>

          {/* Tecnici di supporto */}
          <div>
            <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-2">
              Tecnici di supporto (opzionale)
            </label>
            {available.length === 0 ? (
              <p className="text-xs text-[var(--fc-foreground-muted)] italic">
                {primaryId ? 'Nessun altro tecnico disponibile' : 'Seleziona prima il tecnico responsabile'}
              </p>
            ) : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto rounded-lg border border-[var(--fc-border)] p-2">
                {available.map(t => (
                  <label key={t.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-[var(--fc-surface-overlay)] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={supportIds.includes(t.id)}
                      onChange={() => toggleSupport(t.id)}
                      className="h-4 w-4 rounded border-[var(--fc-border)] text-[var(--fc-primary)]"
                    />
                    <span className="text-sm text-[var(--fc-foreground)]">
                      {t.firstName} {t.lastName}
                      <span className="ml-1.5 text-xs text-[var(--fc-foreground-muted)]">({t.role})</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
            {supportIds.length > 0 && (
              <p className="mt-1.5 text-xs text-[var(--fc-primary)]">
                {supportIds.length} tecnico{supportIds.length > 1 ? 'i' : ''} di supporto selezionat{supportIds.length > 1 ? 'i' : 'o'}
              </p>
            )}
          </div>

          {assignMutation.isError && (
            <p className="text-sm text-red-600">Errore durante l'assegnazione.</p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-[var(--fc-border)] px-5 py-4">
          <button onClick={onClose} className="rounded-lg border border-[var(--fc-border)] px-4 py-2 text-sm">
            Annulla
          </button>
          <button
            onClick={() => assignMutation.mutate()}
            disabled={!primaryId || assignMutation.isPending}
            className="rounded-lg bg-[var(--fc-primary)] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {assignMutation.isPending ? 'Assegnando...' : 'Assegna'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- Main page ----
export function ChecklistDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [showAssign, setShowAssign] = useState(false)
  const [editInfo, setEditInfo] = useState(false)
  const [infoForm, setInfoForm] = useState({ name: '', description: '', frequency: 'monthly' })
  const [editingOpId, setEditingOpId] = useState<string | null>(null)
  const [opForm, setOpForm] = useState({ title: '', description: '', requiresPhoto: false, expectedOutcome: '' })
  const [showAddOp, setShowAddOp] = useState(false)
  const [newOp, setNewOp] = useState({ assetId: '', title: '', description: '', requiresPhoto: false, expectedOutcome: '' })

  const { data: template, isLoading } = useQuery({
    queryKey: ['checklist-templates', id],
    queryFn: async () => (await api.get<Template>(`/checklist-templates/${id}`)).data,
    enabled: Boolean(id),
  })

  const { data: executions = [] } = useQuery<Execution[]>({
    queryKey: ['checklist-executions', id],
    queryFn: async () => (await api.get<Execution[]>(`/maintenance/templates/${id}/executions`)).data,
    enabled: Boolean(id),
  })

  const { data: allTechnicians = [] } = useQuery<Technician[]>({
    queryKey: ['technicians'],
    queryFn: async () => (await api.get<Technician[]>('/technicians')).data,
  })

  const { data: assets = [] } = useQuery<Asset[]>({
    queryKey: ['assets', 'hardware', template?.customerId],
    queryFn: async () => {
      const params: Record<string, string> = { category: 'hardware' }
      if (template?.customerId) params['companyId'] = template.customerId
      return (await api.get<Asset[]>('/assets', { params })).data
    },
    enabled: Boolean(template?.customerId),
  })

  const updateTemplate = useMutation({
    mutationFn: (data: typeof infoForm) => api.put(`/checklist-templates/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['checklist-templates', id] }); setEditInfo(false) },
  })

  const deleteTemplate = useMutation({
    mutationFn: () => api.delete(`/checklist-templates/${id}`),
    onSuccess: () => navigate('/maintenance/checklists'),
  })

  const updateOp = useMutation({
    mutationFn: ({ opId, data }: { opId: string; data: typeof opForm }) =>
      api.put(`/checklist-templates/${id}/operations/${opId}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['checklist-templates', id] }); setEditingOpId(null) },
  })

  const deleteOp = useMutation({
    mutationFn: (opId: string) => api.delete(`/checklist-templates/${id}/operations/${opId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist-templates', id] }),
  })

  const addOp = useMutation({
    mutationFn: () => api.post(`/checklist-templates/${id}/operations`, {
      ...newOp,
      assetId: newOp.assetId || undefined,
      expectedOutcome: newOp.expectedOutcome || undefined,
      description: newOp.description || undefined,
      stepOrder: (template?.operations.length ?? 0),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-templates', id] })
      setShowAddOp(false)
      setNewOp({ assetId: '', title: '', description: '', requiresPhoto: false, expectedOutcome: '' })
    },
  })

  function startEditInfo() {
    if (!template) return
    setInfoForm({ name: template.name, description: template.description ?? '', frequency: template.frequency })
    setEditInfo(true)
  }

  function startEditOp(op: Operation) {
    setOpForm({ title: op.title, description: op.description ?? '', requiresPhoto: op.requiresPhoto, expectedOutcome: op.expectedOutcome ?? '' })
    setEditingOpId(op.id)
  }

  function getTechName(techId: string) {
    const t = allTechnicians.find(x => x.id === techId)
    return t ? `${t.firstName} ${t.lastName}` : techId
  }

  if (isLoading) return <div className="p-8 text-sm text-[var(--fc-foreground-muted)]">Caricamento...</div>
  if (!template) return <div className="p-8 text-sm text-red-600">Template non trovato.</div>

  const sortedOps = [...(template.operations ?? [])].sort((a, b) => a.stepOrder - b.stepOrder)

  return (
    <div className="space-y-6">
      {showAssign && (
        <AssignModal
          templateId={template.id}
          onClose={() => setShowAssign(false)}
          allTechnicians={allTechnicians}
        />
      )}

      <PageHeader
        title={template.name}
        subtitle={`${FREQ_LABELS[template.frequency] ?? template.frequency} · ${sortedOps.length} punti di controllo`}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/maintenance/checklists')}
              className="flex items-center gap-1.5 rounded-lg border border-[var(--fc-border)] px-3 py-2 text-sm text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]"
            >
              <ArrowLeft className="h-4 w-4" /> Indietro
            </button>
            <button
              onClick={startEditInfo}
              className="flex items-center gap-1.5 rounded-lg border border-[var(--fc-border)] px-3 py-2 text-sm"
            >
              <Pencil className="h-4 w-4" /> Modifica
            </button>
            <button
              onClick={() => setShowAssign(true)}
              className="flex items-center gap-1.5 rounded-lg bg-[var(--fc-primary)] px-3 py-2 text-sm font-medium text-white"
            >
              <UserPlus className="h-4 w-4" /> Assegna
            </button>
            <button
              onClick={() => { if (confirm('Eliminare questo template?')) deleteTemplate.mutate() }}
              className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        }
      />

      {/* Info template (view / edit) */}
      <Card>
        {editInfo ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Nome *</label>
                <input
                  value={infoForm.name}
                  onChange={e => setInfoForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Frequenza</label>
                <select
                  value={infoForm.frequency}
                  onChange={e => setInfoForm(p => ({ ...p, frequency: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                >
                  {Object.entries(FREQ_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Descrizione</label>
              <textarea
                value={infoForm.description}
                onChange={e => setInfoForm(p => ({ ...p, description: e.target.value }))}
                rows={2}
                className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm resize-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => updateTemplate.mutate(infoForm)}
                disabled={!infoForm.name || updateTemplate.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                <Check className="h-4 w-4" /> Salva
              </button>
              <button onClick={() => setEditInfo(false)} className="rounded-lg border border-[var(--fc-border)] px-4 py-2 text-sm">
                Annulla
              </button>
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
            {template.description && (
              <div className="col-span-2">
                <dt className="text-xs font-medium text-[var(--fc-foreground-muted)]">Descrizione</dt>
                <dd className="mt-0.5 text-[var(--fc-foreground)]">{template.description}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-[var(--fc-foreground-muted)]">Frequenza</dt>
              <dd className="mt-0.5">
                <span className="inline-flex items-center rounded-full bg-[var(--fc-surface-overlay)] px-2.5 py-0.5 text-xs font-medium text-[var(--fc-foreground)]">
                  {FREQ_LABELS[template.frequency] ?? template.frequency}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-[var(--fc-foreground-muted)]">Punti di controllo</dt>
              <dd className="mt-0.5 text-[var(--fc-foreground)]">{sortedOps.length}</dd>
            </div>
          </dl>
        )}
      </Card>

      {/* Tecnici assegnati */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-[var(--fc-foreground)] flex items-center gap-2">
            <Users className="h-4 w-4 text-[var(--fc-primary)]" />
            Tecnici assegnati
            <span className="text-sm font-normal text-[var(--fc-foreground-muted)]">({executions.length})</span>
          </h2>
          <button
            onClick={() => setShowAssign(true)}
            className="flex items-center gap-1.5 rounded-lg border border-[var(--fc-border)] px-3 py-1.5 text-xs font-medium text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]"
          >
            <Plus className="h-3.5 w-3.5" /> Nuova assegnazione
          </button>
        </div>

        {executions.length === 0 ? (
          <Card>
            <p className="text-sm text-[var(--fc-foreground-muted)] text-center py-4">
              Nessun tecnico assegnato. Usa "Assegna" per assegnare questa checklist.
            </p>
          </Card>
        ) : (
          <div className="grid gap-3">
            {executions.map(exec => {
              const status = STATUS_LABELS[exec.status] ?? { label: exec.status, color: 'bg-gray-100 text-gray-700' }
              const mainTech = exec.technician
                ? `${exec.technician.firstName} ${exec.technician.lastName}`
                : exec.technicianId
                  ? getTechName(exec.technicianId)
                  : '—'
              const supportNames = (exec.supportTechnicianIds ?? []).map(getTechName)

              return (
                <Card key={exec.id}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1.5">
                      {/* Tecnico principale */}
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[var(--fc-primary)] text-xs font-bold text-white">
                          {mainTech.charAt(0)}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-[var(--fc-foreground)]">{mainTech}</p>
                          <p className="text-xs text-[var(--fc-foreground-muted)]">Responsabile</p>
                        </div>
                      </div>

                      {/* Tecnici di supporto */}
                      {supportNames.length > 0 && (
                        <div className="ml-9 space-y-1">
                          {supportNames.map((name, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--fc-surface-overlay)] text-xs font-medium text-[var(--fc-foreground-muted)]">
                                {name.charAt(0)}
                              </span>
                              <p className="text-xs text-[var(--fc-foreground-muted)]">{name} · supporto</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-[var(--fc-foreground-muted)]">
                        <Clock className="h-3 w-3" />
                        {formatDate(exec.createdAt, 'dd/MM/yyyy')}
                      </span>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {/* Punti di controllo */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-[var(--fc-foreground)]">Punti di Controllo</h2>
          <button
            onClick={() => setShowAddOp(true)}
            className="flex items-center gap-1.5 rounded-lg bg-[var(--fc-primary)] px-3 py-1.5 text-xs font-medium text-white"
          >
            <Plus className="h-3.5 w-3.5" /> Aggiungi
          </button>
        </div>

        {/* Form nuovo punto */}
        {showAddOp && (
          <Card>
            <h3 className="text-sm font-semibold mb-3">Nuovo punto di controllo</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Dispositivo</label>
                <select
                  value={newOp.assetId}
                  onChange={e => setNewOp(p => ({ ...p, assetId: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                >
                  <option value="">— nessun dispositivo —</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.name}{a.assetType ? ` (${a.assetType})` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Nome controllo *</label>
                <input
                  value={newOp.title}
                  onChange={e => setNewOp(p => ({ ...p, title: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                  placeholder="Es: Verifica pressione"
                />
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Descrizione</label>
                <input
                  value={newOp.description}
                  onChange={e => setNewOp(p => ({ ...p, description: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={() => setNewOp(p => ({ ...p, requiresPhoto: !p.requiresPhoto }))}
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                  newOp.requiresPhoto
                    ? 'border-[var(--fc-primary)] text-[var(--fc-primary)]'
                    : 'border-[var(--fc-border)] text-[var(--fc-foreground-muted)]'
                }`}
              >
                {newOp.requiresPhoto ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                Foto se NC
              </button>
              <button
                onClick={() => addOp.mutate()}
                disabled={!newOp.title || addOp.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                <Plus className="h-4 w-4" /> Aggiungi
              </button>
              <button onClick={() => setShowAddOp(false)} className="rounded-lg border border-[var(--fc-border)] px-3 py-2 text-sm">
                <X className="h-4 w-4" />
              </button>
            </div>
          </Card>
        )}

        {sortedOps.length === 0 && !showAddOp && (
          <p className="text-sm text-[var(--fc-foreground-muted)]">Nessun punto di controllo. Aggiungine uno.</p>
        )}

        {sortedOps.map((op, idx) => (
          <Card key={op.id}>
            {editingOpId === op.id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Nome controllo *</label>
                    <input
                      value={opForm.title}
                      onChange={e => setOpForm(p => ({ ...p, title: e.target.value }))}
                      className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Descrizione</label>
                    <input
                      value={opForm.description}
                      onChange={e => setOpForm(p => ({ ...p, description: e.target.value }))}
                      className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setOpForm(p => ({ ...p, requiresPhoto: !p.requiresPhoto }))}
                    className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                      opForm.requiresPhoto
                        ? 'border-[var(--fc-primary)] text-[var(--fc-primary)]'
                        : 'border-[var(--fc-border)] text-[var(--fc-foreground-muted)]'
                    }`}
                  >
                    {opForm.requiresPhoto ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                    Foto se NC
                  </button>
                  <button
                    onClick={() => updateOp.mutate({ opId: op.id, data: opForm })}
                    disabled={!opForm.title || updateOp.isPending}
                    className="flex items-center gap-1.5 rounded-lg bg-[var(--fc-primary)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" /> Salva
                  </button>
                  <button onClick={() => setEditingOpId(null)} className="rounded-lg border border-[var(--fc-border)] px-3 py-1.5 text-xs">
                    Annulla
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--fc-primary)] text-xs font-bold text-white mt-0.5">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--fc-foreground)]">{op.title}</p>
                  {op.asset && (
                    <p className="text-xs text-[var(--fc-primary)] mt-0.5">
                      Dispositivo: {op.asset.name}{op.asset.assetType ? ` (${op.asset.assetType})` : ''}
                    </p>
                  )}
                  {op.description && (
                    <p className="text-xs text-[var(--fc-foreground-muted)] mt-0.5">{op.description}</p>
                  )}
                  {op.requiresPhoto && (
                    <span className="mt-1 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
                      Foto se NC
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => startEditOp(op)} className="rounded p-1 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => { if (confirm('Eliminare questo punto?')) deleteOp.mutate(op.id) }}
                    className="rounded p-1 text-[var(--fc-foreground-muted)] hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  )
}
